import React, { useCallback, useState, useMemo } from 'react';
import { getLogger } from 'src/utils/logger';
import { submitBehaviourSupportPlan } from 'src/services/document.service';
import { generateSmartGoals as generateSmartGoalsService } from 'src/services/smart-goals.service';
import { generateStrategies as generateStrategiesService } from 'src/services/strategies.service';
import { useDocumentForm } from 'src/hooks/useDocumentForm';
import { useSmartGoalsGeneration } from 'src/hooks/useSmartGoalsGeneration';
import { useStrategiesGeneration } from 'src/hooks/useStrategiesGeneration';
import { useDocumentDownload } from 'src/hooks/useDocumentDownload';
import { useUsage } from 'src/hooks/useUsage';
import { BehaviourSupportPlanData } from 'src/models/form-types';
import { DocumentDownload } from 'src/components/document-download/DocumentDownload';
import { DocumentResponse } from 'src/models/api-response';
import { UsageIndicator } from 'src/components/common/UsageIndicator';
import { MissingFieldsSummary, MissingField } from 'src/components/common/MissingFieldsSummary';
// Import form sections
import { StudentInfoSection } from './sections/StudentInfoSection';
import { PurposeSection } from './sections/PurposeSection';
import { LearningSection } from './sections/LearningSection';
import { AdditionalInfoSection } from './sections/AdditionalInfoSection';
import { LevelOfAdjustmentSection } from './sections/LevelOfAdjustmentSection';
import { BehavioursSection } from './sections/BehavioursSection';
import { SmartGoalsSection } from './sections/SmartGoalsSection';
import { StrategiesSection } from './sections/StrategiesSection';

const logger = getLogger('BehaviourSupportPlanForm');

interface BehaviourSupportPlanFormProps {
  onBack: () => void;
  onSubmit?: (data: BehaviourSupportPlanData) => void;
}

// Initial form data
const initialFormData: BehaviourSupportPlanData = {
  studentAge: '',
  yearLevel: '',
  strengths: '',
  purpose: [],
  otherPurpose: '',
  learningSupport: [],
  adhdType: '',
  autismLevel: '',
  hasPLASP: false,
  hasRMP: false,
  hasBRP: false,
  otherLearningSupport: '',
  additionalInfo: [],
  otherAdditionalInfo: '',
  levelOfAdjustment: [],
  behaviours: [],
  otherBehaviours: '',
  behaviourContext: [],
  otherBehaviourContext: '',
  behaviourFunction: [],
  otherBehaviourFunction: '',
  behaviourExamples: ''
};

export const BehaviourSupportPlanForm: React.FC<BehaviourSupportPlanFormProps> = ({
  onBack,
  onSubmit
}) => {
  // Usage tracking - get usage from shared context
  const { getUsageForProduct, loading: usageLoading, refresh: refreshUsage } = useUsage();
  const usage = getUsageForProduct('personalised_plan');
  const canUse = usage?.can_use ?? false;

  // Direct document state - for debugging
  const [debugDocumentState, setDebugDocumentState] = useState<{
    documentId: string | null;
    base64Content: string | null;
    showDownload: boolean;
  }>({
    documentId: null,
    base64Content: null,
    showDownload: false
  });

  // Document Download hook
  const {
    isDownloadVisible,
    documentId,
    base64Content,
    showDownload,
    hideDownload
  } = useDocumentDownload();

  // Use the document form hook for form state management
  const {
    formState,
    handleTextChange,
    handleCheckboxChange,
    submitForm,
    isSubmitting
  } = useDocumentForm<BehaviourSupportPlanData, DocumentResponse>({
    initialData: initialFormData,
    submitService: submitBehaviourSupportPlan,
    onSubmitSuccess: response => {
      logger.debug(`Form submission successful:`, response);
      // Show document Download when submission is successful
      if (response && response.documentId) {
        logger.info("About to show Download for document:", response.documentId);
        // Direct state setting for debugging
        setDebugDocumentState({
          documentId: response.documentId,
          base64Content: response.docBase64,
          showDownload: true
        });
        // Use the hook
        showDownload(response);
        // Refresh usage after successful generation
        refreshUsage();
      } else {
        logger.warn("No document ID in response:", response);
      }
      if (onSubmit) {
        onSubmit(formState.formData);
      }
    },
  });

  const generateSmartGoalsWithCount = useCallback(
    (formData: BehaviourSupportPlanData, accessToken: string) => 
      generateSmartGoalsService({...formData, goalType: 'behaviour'}, 3, accessToken),
    []
  );

  const {
    goals,
    isLoading: loadingSmartGoals,
    error: smartGoalsError,
    selectedGoal,
    generateSmartGoals,
    selectGoal
  } = useSmartGoalsGeneration(
    generateSmartGoalsWithCount,
    formState.formData
  );

  const generateStrategiesWithSmartGoal = useCallback(
    (data: { smartGoal: string, numStrategies: number }, accessToken: string) => {
      // Create a clean data object to avoid circular references
      const cleanData = {
        smartGoal: String(data.smartGoal),
        numStrategies: Number(data.numStrategies)
      };
      return generateStrategiesService(cleanData, accessToken);
    },
    []
  );

  const {
    strategies,
    isLoading: loadingStrategies,
    error: strategiesError,
    selectedStrategies,
    generateStrategies,
    toggleStrategySelection
  } = useStrategiesGeneration(
    generateStrategiesWithSmartGoal,
    selectedGoal
  );

  // Handle form submission
  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Check if at least 1 strategy is selected
    if (selectedStrategies.length < 1) {
      logger.warn("Form submission attempted with fewer than 1 strategy");
      return; // Prevent form submission
    }
    
    // Add the selected smart goal and strategies to the form data before submission
    if (selectedGoal) {
      formState.formData.smartGoal = selectedGoal;
    }
    if (selectedStrategies.length > 0) {
      formState.formData.strategies = selectedStrategies;
    }
    logger.info("Submitting form...");
    await submitForm();
  }, [submitForm, formState.formData, selectedGoal, selectedStrategies]);

  // Show the document Download using the direct debug state
  const showDocumentDownloadDebug = debugDocumentState.showDownload &&
    debugDocumentState.documentId &&
    debugDocumentState.base64Content;

  // Check if each section has at least one checkbox selected OR something in the "other" field
  const isSectionValid = useCallback((checkboxArray: string[], otherField: string) => {
    return checkboxArray.length > 0 || otherField.trim() !== '';
  }, []);

  // Special check for learning support section that includes boolean fields
  const isLearningSupportSectionValid = useCallback((
    learningSupport: string[], 
    otherLearningSupport: string,
    hasPLASP: boolean,
    hasRMP: boolean,
    hasBRP: boolean
  ) => {
    return learningSupport.length > 0 || 
           otherLearningSupport.trim() !== '' || 
           hasPLASP || 
           hasRMP || 
           hasBRP;
  }, []);

  // Validate all required sections
  const formValidation = useMemo(() => {
    const { 
      purpose, otherPurpose,
      learningSupport, otherLearningSupport, hasPLASP, hasRMP, hasBRP,
      additionalInfo, otherAdditionalInfo,
      levelOfAdjustment,
      behaviours, otherBehaviours,
      behaviourContext, otherBehaviourContext,
      behaviourFunction, otherBehaviourFunction
    } = formState.formData;

    return {
      isPurposeValid: isSectionValid(purpose, otherPurpose),
      isLearningSupportValid: isLearningSupportSectionValid(
        learningSupport, 
        otherLearningSupport,
        hasPLASP,
        hasRMP,
        hasBRP
      ),
      isAdditionalInfoValid: isSectionValid(additionalInfo, otherAdditionalInfo),
      isLevelOfAdjustmentValid: levelOfAdjustment.length > 0, // This section doesn't have an "other" field
      isBehavioursValid: isSectionValid(behaviours, otherBehaviours),
      isBehaviourContextValid: isSectionValid(behaviourContext, otherBehaviourContext),
      isBehaviourFunctionValid: isSectionValid(behaviourFunction, otherBehaviourFunction),
      // The behaviourExamples field can be left blank as per requirements
    };
  }, [formState.formData, isSectionValid, isLearningSupportSectionValid]);

  // Check if all required sections are valid
  const isFormValid = useMemo(() => {
    return (
      formValidation.isPurposeValid &&
      formValidation.isLearningSupportValid &&
      formValidation.isAdditionalInfoValid &&
      formValidation.isLevelOfAdjustmentValid &&
      formValidation.isBehavioursValid &&
      formValidation.isBehaviourContextValid &&
      formValidation.isBehaviourFunctionValid
    );
  }, [formValidation]);

  // Check if enough strategies are selected
  const hasEnoughStrategies = selectedStrategies.length >= 1;

  // Build a unified list of missing required sections so users can see exactly
  // what still needs to be filled in before they can generate a plan.
  const missingFields = useMemo<MissingField[]>(() => {
    const items: MissingField[] = [];
    if (!formValidation.isPurposeValid) items.push({ label: 'Purpose', anchorId: 'bsp-section-purpose' });
    if (!formValidation.isLearningSupportValid) items.push({ label: 'Learning & Support', anchorId: 'bsp-section-learning' });
    if (!formValidation.isAdditionalInfoValid) items.push({ label: 'Additional information', anchorId: 'bsp-section-additional-info' });
    if (!formValidation.isLevelOfAdjustmentValid) items.push({ label: 'Level of adjustment', anchorId: 'bsp-section-level-of-adjustment' });
    if (!formValidation.isBehavioursValid) items.push({ label: 'Behaviours', anchorId: 'bsp-section-behaviours' });
    if (!formValidation.isBehaviourContextValid) items.push({ label: 'Behaviour context', anchorId: 'bsp-section-behaviours' });
    if (!formValidation.isBehaviourFunctionValid) items.push({ label: 'Behaviour function', anchorId: 'bsp-section-behaviours' });
    if (!hasEnoughStrategies) items.push({ label: 'Select at least one strategy', anchorId: 'bsp-section-strategies' });
    return items;
  }, [formValidation, hasEnoughStrategies]);

  // Determine if we're in the document ready state
  const isDocumentReady = isDownloadVisible || showDocumentDownloadDebug;

  return (
    <div className="bsp-form-container">
      <div className="bsp-form-header">
        <h1>Create a Behaviour Support Plan (BSP)</h1>
        <p className="form-description">
          Help manage and improve an individual's challenging behaviours through proactive and
          positive strategies.
        </p>
      </div>

      {formState.submitError && (
        <div className="form-error-message">
          <p>Error: {formState.submitError}</p>
        </div>
      )}

      {formState.submitSuccess && !isDocumentReady && (
        <div className="form-success-message">
          <p>Your Behaviour Support Plan has been successfully submitted!</p>
        </div>
      )}


      {!isDocumentReady && (
        <form onSubmit={handleSubmit}>
          <StudentInfoSection
            formData={formState.formData}
            onChange={handleTextChange}
          />
          <div id="bsp-section-purpose">
            <PurposeSection
              formData={formState.formData}
              onTextChange={handleTextChange}
              onCheckboxChange={handleCheckboxChange}
            />
          </div>
          <div id="bsp-section-learning">
            <LearningSection
              formData={formState.formData}
              onTextChange={handleTextChange}
              onCheckboxChange={handleCheckboxChange}
            />
          </div>
          <div id="bsp-section-additional-info">
            <AdditionalInfoSection
              formData={formState.formData}
              onTextChange={handleTextChange}
              onCheckboxChange={handleCheckboxChange}
            />
          </div>
          <div id="bsp-section-level-of-adjustment">
            <LevelOfAdjustmentSection
              formData={formState.formData}
              onCheckboxChange={handleCheckboxChange}
            />
          </div>
          <div id="bsp-section-behaviours">
            <BehavioursSection
              formData={formState.formData}
              onTextChange={handleTextChange}
              onCheckboxChange={handleCheckboxChange}
            />
          </div>
          <SmartGoalsSection
            goals={goals}
            isLoading={loadingSmartGoals}
            error={smartGoalsError}
            onGenerateGoals={generateSmartGoals}
            onGoalSelect={selectGoal}
            isFormValid={isFormValid}
            formValidation={formValidation}
          />
          <div id="bsp-section-strategies">
            <StrategiesSection
              strategies={strategies}
              isLoading={loadingStrategies}
              error={strategiesError}
              onGenerateStrategies={generateStrategies}
              onStrategyToggle={toggleStrategySelection}
              selectedStrategies={selectedStrategies}
              smartGoal={selectedGoal}
            />
          </div>
          
          <div className="form-group">
            <div className="form-info-box">
              <p className="info-primary">Gymea documents are all editable Word docs, so you can make changes to wording, fonts or appearance.</p>
              <p className="info-secondary">P.S. Opening your document on mobile devices might cause fonts or formatting to display differently to how they will appear when you open in the Word&nbsp;app.</p>
            </div>
          </div>

          <MissingFieldsSummary missingFields={missingFields} />

          <div className="form-actions">
            <button type="button" className="secondary-button" onClick={onBack}>
              Back
            </button>
            <button
              type="submit"
              className="primary-button"
              disabled={isSubmitting || !isFormValid || !hasEnoughStrategies || !canUse}
            >
              {isSubmitting ? 'Submitting...' : 'Draft a plan'}
            </button>
          </div>

          {/* Usage Indicator - shown below form actions */}
          {usage && (
            <div className="usage-indicators-container">
              <UsageIndicator
                productName={usage.name}
                used={usage.used}
                limit={usage.limit}
                remaining={usage.remaining}
                canUse={usage.can_use}
                period={new Date().toLocaleDateString('en-AU', { year: 'numeric', month: 'long' })}
                loading={usageLoading}
              />
            </div>
          )}
        </form>
      )}
      
      {/* Document Download section - shown when document is ready */}
      {isDocumentReady && (
        <div className="document-download-section">
          {isDownloadVisible && documentId && base64Content ? (
            <DocumentDownload
              documentId={documentId}
              base64Content={base64Content}
              documentType="behaviour-support-plan"
              onClose={hideDownload}
            />
          ) : showDocumentDownloadDebug && (
            <DocumentDownload
              documentId={debugDocumentState.documentId!}
              base64Content={debugDocumentState.base64Content!}
              documentType="behaviour-support-plan"
              onClose={() => setDebugDocumentState(prev => ({...prev, showDownload: false}))}
            />
          )}
          
          <div className="form-actions document-actions">
            <button 
              type="button" 
              className="secondary-button" 
              onClick={
                isDownloadVisible
                  ? hideDownload
                  : () => setDebugDocumentState(prev => ({...prev, showDownload: false}))
              }
            >
              Edit Plan
            </button>
            <button
              type="button"
              className="primary-button"
              onClick={onBack}
            >
              Back to Dashboard
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
